import React, { useState, useContext } from 'react';

import View from 'components/View';
import CoachingViewHeader from 'coaching/components/CoachingViewHeader';
import CoachingHelpHeader from 'coaching/components/CoachingHelpFormView/CoachingHelpHeader';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import './styles/CoachingView.scss';

/**
 * The CoachingViewContext is where we store the sidebar state. In order to toggle a sidebar
 * from a child component, you would do the following:
 *
 * ```
 * import { useCoachingViewState } from 'coaching/components/CoachingView';
 *
 * const { sidebarIsOpen, setSidebar, closeSidebar } = useCoachingViewState();
 * <Button onClick={() => sidebarIsOpen ? closeSidebar() : setSidebar(<span />)}>
 *   Toggle sidebar
 * </Button>
 * ```
 *
 * The sidebar context supports a simple stack, to allow users to go back to a previous
 * sidebar without needing to pass down all the state necessary to render that sidebar again.
 *
 * `pushSidebar` adds the element to the top of the stack, preserving the current sidebar
 * element(s) underneath. `popSidebar` pops the top sidebar off the stack (which closes the
 * sidebar if it was the last one). `setSidebar` replaces the entire stack with the given
 * element, and `closeSidebar` clears the stack.
 *
 * ```
 * function ScheduleSidebar({ schedule }) {
 *   const { pushSidebar } = useCoachingViewState();
 *   return schedule.instances.map(instance =>
 *     <Button onClick={() => pushSidebar(<InstanceSidebar instance={instance} />)}>
 *       View instance
 *     </Button>
 *   )
 * }
 *
 * function InstanceSidebar({ instance }) {
 *   const { popSidebar } = useCoachingViewState();
 *   return (
 *     <Button onClick={popSidebar}>Back to session schedule</Button>
 *   )
 * }
 * ```
 */

type CoachingViewContextType = {
  sidebar: React.ReactElement | undefined;
  sidebarIsOpen: boolean;
  setSidebar: (value: React.ReactElement) => void;
  pushSidebar: (value: React.ReactElement) => void;
  popSidebar: () => void;
  closeSidebar: () => void;
};

const CoachingViewContext = React.createContext<CoachingViewContextType>({
  sidebar: undefined,
  sidebarIsOpen: false,
  setSidebar: (_value: React.ReactElement) => {},
  pushSidebar: (_value: React.ReactElement) => {},
  popSidebar: () => {},
  closeSidebar: () => {},
});

export function useCoachingViewState() {
  return useContext(CoachingViewContext);
}

type Props = {
  children: React.ReactNode;
  helpButtonEventKey?: string;
  headerType?: HeaderType;
  isCoach?: boolean;
  useView?: boolean;
};

export default function CoachingView({
  children,
  helpButtonEventKey,
  headerType = 'default',
  isCoach,
  // useView should be false if a child component being wrapped in CoachingView uses infinite scrolling
  // or else scrolling will break
  useView = true,
}: Props) {
  const [sidebarStack, setSidebarStack] = useState<React.ReactElement[]>([]);
  const sidebar = sidebarStack[0];
  const sidebarIsOpen = !!sidebar;

  const context = {
    sidebar,
    sidebarIsOpen,
    setSidebar: (e: React.ReactElement) => setSidebarStack([e]),
    pushSidebar: (e: React.ReactElement) => setSidebarStack(stack => [e, ...stack]),
    popSidebar: () => setSidebarStack(stack => stack.slice(1)),
    closeSidebar: () => setSidebarStack([]),
  };

  const headerComponentMap: HeaderMap = {
    default: (
      <CoachingViewHeader
        title="Remind Tutoring"
        helpButtonEventKey={helpButtonEventKey}
        isCoach={isCoach}
      />
    ),
    defaultTabbed: (
      <CoachingViewHeader
        isTabbed={true}
        title="Remind Tutoring"
        helpButtonEventKey={helpButtonEventKey}
        isCoach={isCoach}
      />
    ),
    help: <CoachingHelpHeader />,
    helpWithLoginButton: <CoachingHelpHeader showLoginButton />,
    none: <React.Fragment />,
  };

  // on mobile, if the sidebar is open, we want it to just fill the screen
  if (userAgent.phone() && sidebarIsOpen) {
    return (
      <CoachingViewContext.Provider value={context}>
        <div className="CoachingView-sidebar--full">{sidebar}</div>
      </CoachingViewContext.Provider>
    );
  }

  return (
    <CoachingViewContainer context={context} header={headerComponentMap[headerType]}>
      <div className="CoachingView-content">
        {useView ? (
          <View className="CoachingView-scrollContainer" scroll>
            {children}
          </View>
        ) : (
          children
        )}
        {sidebarIsOpen && <div className="CoachingView-sidebar">{sidebar}</div>}
      </div>
    </CoachingViewContainer>
  );
}

function CoachingViewContainer({
  children,
  header,
  context,
}: {
  children: React.ReactNode;
  header: React.ReactNode;
  context: CoachingViewContextType;
}) {
  return (
    <CoachingViewContext.Provider value={context}>
      <div className="CoachingView">
        {header}
        {children}
      </div>
    </CoachingViewContext.Provider>
  );
}

export type HeaderType = 'default' | 'defaultTabbed' | 'help' | 'helpWithLoginButton' | 'none';

type HeaderMap = {
  [K in HeaderType]: React.ReactNode;
};
